/**
 * List view: a dense table of tasks with inline editing, sort, filters,
 * bulk actions, and an inline task composer.
 *
 * Bulk actions (FRIDAY-2): checkboxes on each row plus a sticky footer
 * action bar that can apply milestone / priority / status to every
 * selected task. The bar only renders while at least one row is
 * selected, keeping the default layout identical.
 *
 * FRIDAY-75: the "Sort by" control in the toolbar and the column headers
 * share one sort state, so the table always says what it is sorted by.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LIST_FIELDS, normalizeListFields, type ListField } from '@friday/shared/listFields'
import { getStoredSession, getSessionGeneration } from '@/lib/auth'
import { api } from '@/lib/api'
import { useBoard, useCreateTask, useUpdateTask, type BoardSnapshot } from '@/features/projects/board'
import { useProfiles, pickDisplay, type PublicProfile } from '@/features/profiles/profiles'
import { sortTasks, type SortContext } from '@/features/projects/sorting'
import { toast } from '@/features/feedback/toast'
import { AssigneePicker } from '@/components/task/AssigneePicker'
import { AssigneeChip } from '@/components/user/AssigneeChip'
import { SortSelect, type SortOption } from '@/components/ui/SortSelect'
import { LabelChips } from '@/components/ui/LabelChips'
import { cn } from '@/lib/utils'
import {
  patchForBulkField,
  retainFailedSelection,
  selectionForVisible,
  toggleVisibleSelection,
  type BulkEditField,
  type BulkEditValue,
} from './bulkEdit'
import type { Task } from '@friday/shared/realtime'

type SortKey = ListField

interface ListViewProps {
  wsId: string
  pId: string
  filterSprintId?: string | null
  onOpenTask: (task: Task) => void
}

/** Plain words for the tier the effective layout came from. */
const LAYOUT_SOURCE_LABEL: Record<string, string> = {
  personal: 'Your layout',
  board: 'Board default',
  workspace: 'Workspace default',
  application: 'App default',
}

/**
 * The keys the "Sort by" control offers (FRIDAY-75). They are the list view's
 * own columns, so the control and the table headers always agree. The
 * assignee column is called "Issuer" here.
 */
const LIST_SORT_OPTIONS: SortOption[] = LIST_FIELDS.map(([key, label]) => ({
  value: key,
  label: key === 'assignee' ? 'Issuer' : label,
}))

export function ListView({ wsId, pId, filterSprintId, onOpenTask }: ListViewProps) {
  const board = useBoard(wsId, pId)
  const preferences = useQuery({
    queryKey: ['list-preferences', getStoredSession()?.user.id, getSessionGeneration(), wsId, pId],
    queryFn: () =>
      api<{ fields: ListField[]; source: string; version: number }>(
        `/api/v1/projects/${pId}/list-preferences`,
      ),
  })
  const fields = normalizeListFields(preferences.data?.fields)
  const layoutSource = preferences.data?.source ?? 'application'
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [savingColumns, setSavingColumns] = useState(false)
  const [columnError, setColumnError] = useState('')
  const columnLock = useRef(false)
  async function saveColumns(next: ListField[] | null) {
    if (!preferences.data || columnLock.current) return
    columnLock.current = true
    setSavingColumns(true)
    setColumnError('')
    try {
      await api(`/api/v1/projects/${pId}/list-preferences`, {
        method: 'PUT',
        body: { fields: next, version: preferences.data.version },
        idemKey: crypto.randomUUID(),
      })
      await preferences.refetch()
    } catch (error) {
      setColumnError(error instanceof Error ? error.message : 'Could not save layout. Try again.')
      await preferences.refetch()
    } finally {
      columnLock.current = false
      setSavingColumns(false)
    }
  }
  function moveField(id: ListField, target: ListField) {
    const next = fields.filter((field) => field !== id)
    next.splice(next.indexOf(target), 0, id)
    void saveColumns(normalizeListFields(next))
  }
  const [sortKey, setSortKey] = useState<SortKey>('key')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [filter, setFilter] = useState<'all' | 'mine' | 'overdue'>('all')
  // FRIDAY-2 — selection state for bulk actions.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  // FRIDAY-3 — inline composer.
  const [composerOpen, setComposerOpen] = useState(false)
  const [composerText, setComposerText] = useState('')
  const [composerColumnId, setComposerColumnId] = useState<string | null>(null)

  const columns = board.data?.columns ?? []
  // Pick a sensible default column once the board loads. Prefer "To Do" but
  // fall back to the first column if the project hasn't been customised.
  const defaultColumnId = columns.find((c) => c.kind === 'todo')?.id ?? columns[0]?.id ?? null
  const activeComposerColumnId = composerColumnId ?? defaultColumnId

  // The assignees are people, so the table needs their display names and
  // avatars (FRIDAY-75). One batched request for the whole board.
  const assigneeIds = useMemo(
    () => [
      ...new Set(
        (board.data?.tasks ?? [])
          .map((t) => t.assigneeUserId)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    ],
    [board.data?.tasks],
  )
  const { profiles } = useProfiles(assigneeIds)

  const sortContext: SortContext = useMemo(
    () => ({
      nameOf: (userId) => pickDisplay(userId, profiles[userId]).displayName,
      columnRank: (columnId) =>
        (board.data?.columns ?? []).find((c) => c.id === columnId)?.position ?? 0,
      sprintName: (sprintId) =>
        sprintId ? ((board.data?.sprints ?? []).find((s) => s.id === sprintId)?.name ?? null) : null,
      milestoneName: (milestoneId) =>
        milestoneId
          ? ((board.data?.milestones ?? []).find((m) => m.id === milestoneId)?.name ?? null)
          : null,
    }),
    [profiles, board.data],
  )

  const rows = useMemo(() => {
    if (!board.data) return []
    let r = board.data.tasks
    if (filterSprintId) r = r.filter((t) => t.sprintId === filterSprintId)
    if (filter === 'overdue') r = r.filter((t) => t.dueDate && new Date(t.dueDate) < new Date())
    return sortTasks(r, sortKey, sortDir, sortContext)
  }, [board.data, filterSprintId, sortKey, sortDir, filter, sortContext])

  function setSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(k)
      setSortDir('asc')
    }
  }

  // Selection is intentionally scoped to this board/view. Changing project or
  // visible filters drops hidden rows instead of carrying them into an action.
  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows])
  useEffect(() => {
    setSelected((current) => selectionForVisible(current, visibleIds))
  }, [pId, filterSprintId, filter, visibleIds])

  const selectedVisible = useMemo(
    () => [...selected].filter((id) => visibleIds.has(id)),
    [selected, visibleIds],
  )
  const allVisibleSelected = rows.length > 0 && selectedVisible.length === rows.length

  function toggleAll() {
    setSelected((current) => toggleVisibleSelection(current, visibleIds, !allVisibleSelected))
  }
  function toggleOne(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function clearSelection() {
    setSelected(new Set())
  }

  if (board.isLoading) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">Loading list…</div>
    )
  }
  if (board.error) {
    return <div className="flex h-full items-center justify-center text-sm text-red-500">Failed to load.</div>
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-surface-200 dark:border-surface-700 flex flex-wrap items-center gap-2 border-b px-4 py-2 text-xs">
        <div className="flex items-center gap-1">
          {(['all', 'mine', 'overdue'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'rounded-md px-2 py-0.5 capitalize',
                filter === f
                  ? 'bg-brand-500/10 text-brand-600 dark:text-brand-300'
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800',
              )}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={columnsOpen}
          onClick={() => setColumnsOpen(!columnsOpen)}
          className="btn-ghost text-xs"
        >
          Columns
        </button>
        <SortSelect
          value={sortKey}
          options={LIST_SORT_OPTIONS}
          direction={sortDir}
          onChange={(value) => {
            setSortKey(value as SortKey)
            setSortDir('asc')
          }}
          onDirectionChange={setSortDir}
        />
        {preferences.data && (
          <span className="text-surface-500" data-testid="list-layout-source">
            Layout: {LAYOUT_SOURCE_LABEL[layoutSource] ?? LAYOUT_SOURCE_LABEL.application}
          </span>
        )}
        {/* Only a personal layout has anything to reset: dropping it falls
            back to the board default (which may itself inherit). */}
        {layoutSource === 'personal' && (
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={savingColumns}
            onClick={() => void saveColumns(null)}
          >
            Reset to board default
          </button>
        )}
        <span role="status" className="text-surface-500">
          {savingColumns ? 'Saving…' : ''}
        </span>
        <span className="text-surface-500 ml-auto">
          {rows.length} task{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      {columnError && (
        <p role="alert" className="border-b px-4 py-2 text-xs text-red-600 dark:text-red-300">
          {columnError} Try the change again.
        </p>
      )}
      {columnsOpen && (
        <div className="max-h-80 overflow-auto border-b p-3 text-sm" aria-label="List columns">
          <p>Column display order (task sorting is controlled by table headers).</p>
          {preferences.isError && (
            <p role="alert">
              Could not load layout. <button onClick={() => void preferences.refetch()}>Retry</button>
            </p>
          )}
          {[...fields, ...LIST_FIELDS.map(([id]) => id).filter((id) => !fields.includes(id))].map((id) => (
            <div
              key={id}
              className="flex items-center gap-2 py-1"
              draggable={id !== 'title' && fields.includes(id) && !savingColumns}
              onDragStart={(e) => e.dataTransfer.setData('text/plain', id)}
              onDragOver={(e) => {
                if (id !== 'title') e.preventDefault()
              }}
              onDrop={(e) => {
                e.preventDefault()
                const from = e.dataTransfer.getData('text/plain') as ListField
                if (from !== 'title' && from !== id && fields.includes(from) && fields.includes(id))
                  moveField(from, id)
              }}
            >
              <label>
                <input
                  type="checkbox"
                  checked={fields.includes(id)}
                  disabled={id === 'title' || savingColumns || !preferences.data}
                  onChange={(e) =>
                    void saveColumns(e.target.checked ? [...fields, id] : fields.filter((f) => f !== id))
                  }
                />{' '}
                {LIST_FIELDS.find(([key]) => key === id)?.[1]}
                {id === 'title' && ' (required, first)'}
              </label>
              {id !== 'title' && fields.includes(id) && (
                <>
                  <button
                    aria-label={`Move ${id} left`}
                    disabled={savingColumns || fields.indexOf(id) <= 1}
                    onClick={() => moveField(id, fields[fields.indexOf(id) - 1]!)}
                  >
                    ←
                  </button>
                  <button
                    aria-label={`Move ${id} right`}
                    disabled={savingColumns || fields.indexOf(id) === fields.length - 1}
                    onClick={() => {
                      const next = [...fields]
                      const i = next.indexOf(id)
                      ;[next[i], next[i + 1]] = [next[i + 1]!, next[i]!]
                      void saveColumns(next)
                    }}
                  >
                    →
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table className="w-full min-w-max text-sm">
          <thead className="dark:bg-surface-900/80 sticky top-0 z-10 bg-white/80 backdrop-blur">
            <tr className="border-surface-200 dark:border-surface-700 text-surface-500 border-b text-left text-[11px] uppercase tracking-wider">
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all visible rows"
                  className="border-surface-300 h-3.5 w-3.5 cursor-pointer rounded"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allVisibleSelected && selectedVisible.length > 0
                  }}
                  onChange={toggleAll}
                />
              </th>
              {fields.map((id) => (
                <Th key={id} k={id} sortKey={sortKey} sortDir={sortDir} onClick={() => setSort(id)}>
                  {LIST_FIELDS.find(([key]) => key === id)?.[1]}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <Row
                key={t.id}
                fields={fields}
                milestones={board.data?.milestones ?? []}
                sprints={board.data?.sprints ?? []}
                task={t}
                profiles={profiles}
                projectColumns={columns}
                wsId={wsId}
                pId={pId}
                onOpen={() => onOpenTask(t)}
                selected={selected.has(t.id)}
                onToggle={() => toggleOne(t.id)}
              />
            ))}
            {rows.length === 0 && !composerOpen && (
              <tr>
                <td colSpan={fields.length + 1} className="text-surface-500 px-4 py-12 text-center text-sm">
                  No tasks here yet.
                </td>
              </tr>
            )}
            <ComposerRow
              colSpan={fields.length + 1}
              wsId={wsId}
              pId={pId}
              open={composerOpen}
              onOpenChange={setComposerOpen}
              text={composerText}
              onTextChange={setComposerText}
              columnId={activeComposerColumnId}
              onColumnChange={setComposerColumnId}
              columns={columns}
              onCreated={() => {
                setComposerText('')
                setComposerOpen(false)
              }}
            />
          </tbody>
        </table>
      </div>
      <BulkActionBar
        visible
        count={selectedVisible.length}
        wsId={wsId}
        pId={pId}
        scopeKey={`${wsId}:${pId}:${filterSprintId ?? ''}:${filter}`}
        selectedTasks={rows.filter((t) => selected.has(t.id))}
        milestones={board.data?.milestones ?? []}
        members={board.data?.members ?? []}
        onRetain={(failedIds) => setSelected((current) => retainFailedSelection(current, failedIds))}
        onClear={clearSelection}
      />
    </div>
  )
}

function Th({
  k,
  sortKey,
  sortDir,
  onClick,
  children,
}: {
  k: SortKey
  sortKey: SortKey
  sortDir: 'asc' | 'desc'
  onClick: () => void
  children: React.ReactNode
}) {
  const active = k === sortKey
  return (
    <th
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'cursor-pointer select-none px-3 py-2 font-semibold',
        active && 'text-brand-600 dark:text-brand-300',
      )}
    >
      <button type="button" onClick={onClick}>
        {children}
        {active && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  )
}

function Row({
  fields,
  milestones,
  sprints,
  task,
  profiles,
  projectColumns,
  wsId,
  pId,
  onOpen,
  selected,
  onToggle,
}: {
  fields: ListField[]
  milestones: Array<{ id: string; name: string }>
  sprints: Array<{ id: string; name: string }>
  task: Task
  profiles: Record<string, PublicProfile>
  projectColumns: Array<{ id: string; name: string; color: string }>
  wsId: string
  pId: string
  onOpen: () => void
  selected: boolean
  onToggle: () => void
}) {
  const col = projectColumns.find((c) => c.id === task.columnId)
  const update = useUpdateTask(wsId, pId)
  return (
    <tr
      onClick={onOpen}
      className={cn(
        'border-surface-100 dark:border-surface-800 cursor-pointer border-b',
        selected
          ? 'bg-brand-500/5 hover:bg-brand-500/10'
          : 'hover:bg-surface-50 dark:hover:bg-surface-800/40',
      )}
    >
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Select ${task.key}`}
          className="border-surface-300 h-3.5 w-3.5 cursor-pointer rounded"
          checked={selected}
          onChange={onToggle}
        />
      </td>
      {fields.map((field) => {
        switch (field) {
          case 'key':
            return (
              <td key={field} className="text-surface-500 px-3 py-2 font-mono text-[11px]">
                {task.key}
              </td>
            )
          case 'title':
            return (
              <td key={field} className="max-w-md truncate px-3 py-2">
                <span className="text-surface-900 dark:text-surface-50">{task.title}</span>
                {task.subtaskCount > 0 && (
                  <span className="text-surface-500 ml-2 text-[10px]">
                    ☑ {task.subtaskDoneCount}/{task.subtaskCount}
                  </span>
                )}
              </td>
            )
          case 'status':
            return (
              <td key={field} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                <select
                  value={task.columnId}
                  onChange={(e) =>
                    update.mutate({
                      taskId: task.id,
                      version: task.version,
                      patch: { columnId: e.target.value },
                    })
                  }
                  className="rounded px-1.5 py-0.5 text-xs"
                >
                  {projectColumns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {col && (
                  <span
                    className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: col.color }}
                    title={col.name}
                  />
                )}
              </td>
            )
          case 'priority':
            return (
              <td key={field} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                <select
                  value={task.priority}
                  onChange={(e) =>
                    update.mutate({
                      taskId: task.id,
                      version: task.version,
                      patch: { priority: e.target.value },
                    })
                  }
                  className="rounded px-1.5 py-0.5 text-xs"
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </td>
            )
          case 'assignee':
            return (
              <td key={field} className="px-3 py-2 text-xs">
                {/* The assignee is a person: avatar + name (FRIDAY-75). */}
                <AssigneeChip userId={task.assigneeUserId} profiles={profiles} size="xs" />
              </td>
            )
          case 'points':
            return (
              <td key={field} className="px-3 py-2 font-mono text-xs">
                {task.storyPoints ?? '—'}
              </td>
            )
          case 'due':
            return (
              <td
                key={field}
                className={cn(
                  'px-3 py-2 text-xs',
                  task.dueDate && new Date(task.dueDate) < new Date() && 'text-red-500',
                )}
              >
                {task.dueDate ? task.dueDate.slice(0, 10) : '—'}
              </td>
            )
          case 'milestone':
            return (
              <td key={field} className="px-3 py-2 text-xs">
                {milestones.find((m) => m.id === task.milestoneId)?.name ?? '—'}
              </td>
            )
          case 'sprint':
            return (
              <td key={field} className="px-3 py-2 text-xs">
                {sprints.find((m) => m.id === task.sprintId)?.name ?? '—'}
              </td>
            )
          case 'start':
            return (
              <td key={field} className="px-3 py-2 text-xs">
                {task.startDate?.slice(0, 10) ?? '—'}
              </td>
            )
          case 'labels':
            return (
              <td key={field} className="px-3 py-2 text-xs">
                <LabelChips labels={task.labels ?? []} />
              </td>
            )
        }
      })}
    </tr>
  )
}

/**
 * Inline composer row (FRIDAY-3, list view). Appears at the bottom of the
 * task table — click to open, type a title, Enter to submit. Mirrors the
 * kanban composer pattern but inline.
 */
function ComposerRow({
  colSpan,
  wsId,
  pId,
  open,
  onOpenChange,
  text,
  onTextChange,
  columnId,
  onColumnChange,
  columns,
  onCreated,
}: {
  colSpan: number
  wsId: string
  pId: string
  open: boolean
  onOpenChange: (v: boolean) => void
  text: string
  onTextChange: (v: string) => void
  columnId: string | null
  onColumnChange: (v: string | null) => void
  columns: Array<{ id: string; name: string; color: string }>
  onCreated: () => void
}) {
  const create = useCreateTask(wsId, pId, columnId ?? undefined)
  if (!open) {
    return (
      <tr
        onClick={() => onOpenChange(true)}
        className="border-surface-100 dark:border-surface-800 text-surface-500 hover:bg-surface-50 dark:hover:bg-surface-800/40 cursor-pointer border-b"
      >
        <td colSpan={colSpan} className="px-3 py-2 text-xs">
          <span className="font-medium">+ Add task</span>
          <span className="text-surface-500 ml-2 text-[10px]">
            {columns.find((c) => c.id === columnId)?.name ?? 'First column'}
          </span>
        </td>
      </tr>
    )
  }
  return (
    <tr className="border-surface-100 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/30 border-b">
      <td colSpan={colSpan} className="px-3 py-2">
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            const t = text.trim()
            if (!t) return
            try {
              await create.mutateAsync({ title: t, priority: 'medium' })
              onCreated()
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Failed to create task', { kind: 'error' })
            }
          }}
          className="flex items-center gap-2"
        >
          <input
            autoFocus
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onOpenChange(false)
                onTextChange('')
              }
            }}
            placeholder="New task title…"
            className="input flex-1 text-sm"
          />
          <select
            value={columnId ?? ''}
            onChange={(e) => onColumnChange(e.target.value || null)}
            className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1 text-xs"
          >
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-primary text-xs" disabled={!text.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Add'}
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => {
              onOpenChange(false)
              onTextChange('')
            }}
          >
            Cancel
          </button>
        </form>
      </td>
    </tr>
  )
}

/** One field is staged and applied per operation; task names/system fields are never offered. */
function BulkActionBar({
  visible, count, wsId, pId, scopeKey, selectedTasks, milestones, members, onRetain, onClear,
}: {
  visible: boolean
  count: number
  wsId: string
  pId: string
  scopeKey: string
  selectedTasks: Task[]
  milestones: Array<{ id: string; name: string; projectId?: string }>
  members: Array<{ userId: string }>
  onRetain: (failedIds: Set<string>) => void
  onClear: () => void
}) {
  const update = useUpdateTask(wsId, pId)
  const queryClient = useQueryClient()
  const [field, setField] = useState<BulkEditField>('dueDate')
  const [value, setValue] = useState<BulkEditValue>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [lastFailedIds, setLastFailedIds] = useState<string[]>([])
  const lastOperation = useRef<{ failedIds: string[]; patch: Record<string, unknown>; label: string; scopeKey: string } | null>(null)
  // State updates are asynchronous. The synchronous lock closes the
  // double-click/same-tick window before React can render the pending button.
  // An ID means a stale finally block cannot unlock a newer operation.
  const activeApplyId = useRef<number | null>(null)
  const nextApplyId = useRef(0)
  const scopeEpoch = useRef(0)
  const currentScope = useRef(scopeKey)
  const selectedKey = selectedTasks.map((task) => task.id).sort().join(':')
  const currentSelectionKey = useRef(selectedKey)
  const expectedSelectionKey = useRef(selectedKey)

  useEffect(() => setValue(null), [field])

  // A board/filter change invalidates both an old retry and any in-flight
  // completion. Layout timing prevents an old promise from publishing results
  // into the newly rendered scope.
  useLayoutEffect(() => {
    if (currentScope.current !== scopeKey) {
      currentScope.current = scopeKey
      scopeEpoch.current += 1
      lastOperation.current = null
      setLastFailedIds([])
      setValue(null)
      // A scope change makes the old result irrelevant. Release its pending
      // UI immediately, but leave the promise running; its ID prevents its
      // finally block from releasing a newer operation.
      activeApplyId.current = null
      setIsApplying(false)
    }
  }, [scopeKey])

  useLayoutEffect(() => {
    currentSelectionKey.current = selectedKey
    if (expectedSelectionKey.current !== selectedKey) {
      lastOperation.current = null
      setLastFailedIds([])
      expectedSelectionKey.current = selectedKey
    }
  }, [selectedKey])

  async function freshSelectedTasks(ids: readonly string[]) {
    const key = ['board', wsId, pId] as const
    const snapshot = await queryClient.fetchQuery<BoardSnapshot>({
      queryKey: key,
      queryFn: () => api<BoardSnapshot>(`/api/v1/projects/${pId}/board`),
      staleTime: 0,
    })
    const byId = new Map(snapshot.tasks.map((task) => [task.id, task]))
    // Only current visible selection may be sent. Fresh tasks provide current
    // versions, avoiding an automatic second 409 on retry.
    return ids.map((id) => byId.get(id)).filter((task): task is Task => Boolean(task))
  }

  async function apply(ids: readonly string[], patch: Record<string, unknown>, label: string) {
    if (activeApplyId.current !== null || ids.length === 0) return
    const applyId = ++nextApplyId.current
    activeApplyId.current = applyId
    setIsApplying(true)
    const startEpoch = scopeEpoch.current
    const startScope = scopeKey
    const startSelection = selectedKey
    try {
      const tasks = await freshSelectedTasks(ids)
      if (currentScope.current !== startScope || scopeEpoch.current !== startEpoch || currentSelectionKey.current !== startSelection) return
      const results = await Promise.allSettled(tasks.map((task) =>
        update.mutateAsync({ taskId: task.id, version: task.version, patch }),
      ))
      if (currentScope.current !== startScope || scopeEpoch.current !== startEpoch || currentSelectionKey.current !== startSelection) return
      const failedIds = tasks
        .filter((_, index) => results[index]?.status === 'rejected')
        .map((task) => task.id)
        .sort()
      if (failedIds.length) {
        // Retaining failed rows is the one selection transition owned by this
        // operation; all other selection transitions discard retry state.
        expectedSelectionKey.current = failedIds.join(':')
        lastOperation.current = { failedIds, patch, label, scopeKey: startScope }
        setLastFailedIds(failedIds)
        onRetain(new Set(failedIds))
        toast(`${label}: ${tasks.length - failedIds.length}/${tasks.length} updated. ${failedIds.length} kept selected to retry.`, { kind: 'error' })
      } else {
        lastOperation.current = null
        setLastFailedIds([])
        onClear()
        toast(`${label}: ${tasks.length} updated`, { kind: 'success' })
      }
    } catch (error) {
      if (currentScope.current === startScope && scopeEpoch.current === startEpoch) {
        toast(error instanceof Error ? error.message : `${label} failed`, { kind: 'error' })
      }
    } finally {
      if (activeApplyId.current === applyId) {
        activeApplyId.current = null
        setIsApplying(false)
      }
    }
  }

  function submit() {
    try {
      const patch = patchForBulkField(field, value)
      if (field === 'milestoneId' && value !== null && !milestones.some((m) => m.id === value && (!m.projectId || m.projectId === pId))) throw new Error('That milestone is not in this project')
      if (field === 'assigneeUserId' && value !== null && !members.some((m) => m.userId === value)) throw new Error('That assignee is not in this workspace')
      void apply(selectedTasks.map((task) => task.id), patch, field === 'dueDate' ? 'Due date updated' : `${field.replace('UserId', '')} updated`)
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Choose a valid value', { kind: 'error' })
    }
  }

  function retry() {
    const operation = lastOperation.current
    if (!operation || operation.scopeKey !== scopeKey || operation.failedIds.join(':') !== selectedKey) return
    void apply(operation.failedIds, operation.patch, `Retry ${operation.label}`)
  }

  if (!visible || count === 0) return null

  return (
    <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-brand-500/40 bg-brand-500/10 px-4 py-2 text-xs backdrop-blur">
      <span className="font-medium">{count} selected</span>
      <button onClick={onClear} disabled={isApplying} className="rounded px-1.5 py-0.5 text-surface-600 hover:bg-surface-100 disabled:opacity-50">Clear</button>
      <select aria-label="Bulk edit field" value={field} disabled={isApplying} onChange={(e) => setField(e.target.value as BulkEditField)} className="rounded border border-surface-300 bg-white px-2 py-1 dark:border-surface-700 dark:bg-surface-800">
        <option value="dueDate">Due date</option><option value="assigneeUserId">Assignee</option><option value="storyPoints">Story points</option><option value="milestoneId">Milestone</option><option value="status">Status</option>
      </select>
      {field === 'dueDate' && <input aria-label="Due date" type="date" disabled={isApplying} value={typeof value === 'string' ? value : ''} onChange={(e) => setValue(e.target.value || null)} className="rounded border border-surface-300 bg-white px-2 py-1 dark:border-surface-700 dark:bg-surface-800" />}
      {field === 'assigneeUserId' && <AssigneePicker value={typeof value === 'string' ? value : null} candidates={members.map((m) => ({ id: m.userId }))} onChange={setValue} disabled={isApplying} />}
      {field === 'storyPoints' && <input aria-label="Story points" type="number" min="0" max="99" step="1" disabled={isApplying} value={typeof value === 'number' ? value : ''} onChange={(e) => setValue(e.target.value === '' ? null : Number(e.target.value))} className="w-20 rounded border border-surface-300 bg-white px-2 py-1 dark:border-surface-700 dark:bg-surface-800" />}
      {field === 'milestoneId' && <select aria-label="Milestone" disabled={isApplying} value={typeof value === 'string' ? value : ''} onChange={(e) => setValue(e.target.value || null)} className="rounded border border-surface-300 bg-white px-2 py-1 dark:border-surface-700 dark:bg-surface-800"><option value="">No milestone</option>{milestones.filter((m) => !m.projectId || m.projectId === pId).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>}
      {field === 'status' && <select aria-label="Status" disabled={isApplying} value={typeof value === 'string' ? value : ''} onChange={(e) => setValue(e.target.value)} className="rounded border border-surface-300 bg-white px-2 py-1 dark:border-surface-700 dark:bg-surface-800"><option value="">Choose status</option><option value="open">Open</option><option value="in_progress">In progress</option><option value="in_review">In review</option><option value="done">Done</option><option value="blocked">Blocked</option><option value="cancelled">Cancelled</option></select>}
      <button type="button" onClick={submit} disabled={isApplying} className="btn-primary text-xs disabled:opacity-50">{isApplying ? 'Applying…' : 'Apply'}</button>
      {lastFailedIds.length > 0 && <button type="button" onClick={retry} disabled={isApplying} className="btn-ghost text-xs">Retry {lastFailedIds.length} failed</button>}
      <span className="text-surface-500">Clear the value to remove a due date, assignee, points, or milestone.</span>
    </div>
  )
}
